import { useState } from "react";
import { Link } from "react-router-dom";
import { C } from "../constants/theme";
import { useSiteContent } from "../context/SiteContentContext";
import FadeIn from "../components/ui/FadeIn";
import TiltCard from "../components/ui/TiltCard";
import RainbowDivider from "../components/ui/RainbowDivider";
import NafsTree from "../components/ui/NafsTree";
import BloomsTaxonomy from "../components/ui/BloomsTaxonomy";

const SUBJECTS = [
  { icon:"📖", color:"#4EA8DE", title:"Quran — Tajweed, Qaida & Nazra",
    body:"Correct recitation, letter by letter, sound by sound.",
    why:"Every letter recited correctly is a door to a lifetime of worship done right." },
  { icon:"🕋", color:"#D9534F", title:"Islamic Aqaid",
    body:"From the Quran and Hadith — belief taught at its source.",
    why:"A child who knows what they believe, and why, can't be easily shaken later." },
  { icon:"📿", color:"#9B59B6", title:"Hifz of Selected Surahs, with Tafseer",
    body:"Memorised with understanding, not just recitation.",
    why:"What's memorised with meaning stays for life — not just for a test." },
  { icon:"🗨️", color:"#E2748F", title:"Language & Literacy (English)",
    body:"Storytime, vocabulary, and early writing.",
    why:"The words a child has are the world they can describe — and later, defend." },
  { icon:"🗣️", color:"#7396DD", title:"Urdu",
    body:"Reading, speaking, and everyday conversation.",
    why:"Rooted in the language of home, community, and belonging." },
  { icon:"🕌", color:"#6CB566", title:"Arabic",
    body:"The language of the Quran, taught from the earliest years.",
    why:"So the Quran is never a foreign language to your child — it's their own." },
  { icon:"🔢", color:"#F0AE1E", title:"Numeracy",
    body:"Counting, sorting, and shape recognition — hands before pencils.",
    why:"Numbers make sense first through the hands, long before they make sense on paper." },
  { icon:"🔬", color:"#4BAE95", title:"Science & STEM",
    body:"Weather, plants, animals, and hands-on discovery.",
    why:"Every 'why' opens into 'Allah made it so' — curiosity taught as a form of worship." },
  { icon:"💛", color:"#F0876A", title:"Social-Emotional Learning",
    body:"Sharing, empathy, and self-control.",
    why:"The skill that decides whether every other skill gets used for good." },
  { icon:"🧠", color:"#8B6BE8", title:"Executive Functioning",
    body:"Focus, planning, and working memory.",
    why:"The quiet skill behind every other skill — the ability to actually follow through." },
  { icon:"🥋", color:"#D9534F", title:"Physical Classes (Taekwondo)",
    body:"Discipline, coordination, and confidence in the body.",
    why:"A body that knows its own strength rarely needs to prove it to anyone else." },
  { icon:"🌍", color:"#4EA8DE", title:"Life Skills & Sense of the World",
    body:"Everyday independence and understanding the world around them.",
    why:"The difference between a child who can recite a lesson and one who can live it." },
];

const TRACKING = [
  { icon:"🚌", color:C.coral, title:"Monthly Field Trips",       body:"Farm visits, mall visits, and more, chosen to fit what the children are learning that month — real-world exposure beyond the classroom." },
  { icon:"🧠", color:C.navy,  title:"Behavioural Analysis",      body:"A child psychologist conducts play-based developmental checks. Concerns are caught early, and reports go to parents." },
  { icon:"📊", color:"#8B6BE8", title:"Academic & Learning Progress", body:"Every child's progress is observed and tracked individually — never measured against another child's timeline." },
  { icon:"🥗", color:C.mint,  title:"Nutritional Profile Tracking & Assistance", body:"A paediatric nutritionist tracks each child's profile and assists families with personalised guidance." },
];

const Preschool = () => {
  const { content } = useSiteContent();
  const PEDAGOGY = Array.isArray(content.programsPedagogy) ? content.programsPedagogy : [];
  const [openFormat, setOpenFormat] = useState(null);
  const [activeSubject, setActiveSubject] = useState(0);

  return (
  <div style={{ paddingTop: 72 }}>
    {/* ── Hero ── */}
    <section style={{ background:`linear-gradient(160deg,${C.coral} 0%,#d4623d 100%)`, padding:"100px 6vw 90px", position:"relative", overflow:"hidden", minHeight:"52vh", display:"flex", alignItems:"center" }}>
      <div style={{ position:"absolute", top:-80, right:-80, width:450, height:450, borderRadius:"50%", background:"rgba(255,255,255,0.07)", pointerEvents:"none" }}/>
      <div style={{ maxWidth:1100, margin:"0 auto", position:"relative", zIndex:2 }}>
        <Link to="/programs" style={{ display:"inline-flex", alignItems:"center", gap:6, fontFamily:"Nunito", fontWeight:700, fontSize:12.5, color:"rgba(255,255,255,0.75)", textDecoration:"none", marginBottom:16 }}>
          ← Back to All Programmes
        </Link>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(255,255,255,0.18)", color:C.white, fontFamily:"Nunito", fontWeight:700, fontSize:11, letterSpacing:"0.15em", padding:"6px 16px", borderRadius:100, textTransform:"uppercase", marginBottom:20 }}>
          Ages 4 – 7 · Opening January 2027
        </div>
        <h1 style={{ fontFamily:"Fredoka One", fontSize:"clamp(30px,5vw,58px)", color:C.white, lineHeight:1.1, margin:"0 0 20px", maxWidth:700 }}>
          The Mini Muslims Nest<br/><span style={{ color:C.yellow }}>Preschool.</span>
        </h1>
        <p style={{ fontFamily:"Lora", fontStyle:"italic", fontSize:"clamp(15px,2vw,19px)", color:"rgba(255,255,255,0.88)", maxWidth:620, lineHeight:1.82, margin:0 }}>
          Six years of curriculum research and live testing through our Afterschool Club, becoming a dedicated early-years programme — small by design, and built around your child's own pace.
        </p>
      </div>
    </section>

    {/* ── Small by design ── */}
    <section style={{ background:C.cream, padding:"70px 6vw" }}>
      <div style={{ maxWidth:900, margin:"0 auto" }}>
        <FadeIn>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:20 }}>
            <div style={{ background:C.white, borderRadius:18, padding:"24px 22px", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", borderTop:`5px solid ${C.coral}` }}>
              <div style={{ fontFamily:"Fredoka One", fontSize:22, color:C.coral, marginBottom:6 }}>Max 15 Families, Max 15 Children</div>
              <p style={{ fontFamily:"Nunito", fontSize:13, color:C.muted, lineHeight:1.7, margin:0 }}>In the whole school — not per class. Other schools chase higher numbers; we chase higher quality, and that only works small.</p>
            </div>
            <div style={{ background:C.white, borderRadius:18, padding:"24px 22px", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", borderTop:`5px solid ${C.mint}` }}>
              <div style={{ fontFamily:"Fredoka One", fontSize:22, color:C.mint, marginBottom:6 }}>Mixed-Age Classroom</div>
              <p style={{ fontFamily:"Nunito", fontSize:13, color:C.muted, lineHeight:1.7, margin:0 }}>Montessori-inspired, with children of different ages learning side by side, the way siblings and communities naturally do.</p>
            </div>
            <div style={{ background:C.white, borderRadius:18, padding:"24px 22px", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", borderTop:`5px solid ${C.yellow}` }}>
              <div style={{ fontFamily:"Fredoka One", fontSize:22, color:"#D89400", marginBottom:6 }}>Your Child's Own Pace</div>
              <p style={{ fontFamily:"Nunito", fontSize:13, color:C.muted, lineHeight:1.7, margin:0 }}>Fast-paced children can complete the programme early. Those who need more time are given exactly that — no rush, either way.</p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>

    {/* ── Five dimensions ── */}
    <section style={{ background:C.warmGray, padding:"90px 6vw" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:56 }}>
            <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.coral, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>Five Dimensions</div>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(26px,4vw,44px)", color:C.navy, margin:"0 0 14px" }}>Every Subject Lives in One of Five Dimensions.</h2>
            <RainbowDivider />
            <p style={{ fontFamily:"Nunito", fontSize:15, color:C.muted, maxWidth:600, margin:"0 auto", lineHeight:1.8 }}>
              Every subject is taught, but never in isolation — each one is woven into who your child is, as Allah created them.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.1}>
          <NafsTree />
        </FadeIn>
      </div>
    </section>

    {/* ── What they learn ── */}
    <section style={{ background:C.cream, padding:"90px 6vw" }}>
      <div style={{ maxWidth:1000, margin:"0 auto" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:44 }}>
            <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>What They Learn</div>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(26px,4vw,44px)", color:C.navy, margin:"0 0 14px" }}>The Full Curriculum.</h2>
            <RainbowDivider />
            <p style={{ fontFamily:"Nunito", fontSize:14, color:C.muted, maxWidth:560, margin:"0 auto" }}>
              Twelve subjects, each one substantial on its own. Tap any to see why it's there.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.06}>
          <div style={{ display:"flex", gap:8, flexWrap:"wrap", justifyContent:"center", marginBottom:32 }}>
            {SUBJECTS.map(({ icon, color, title }, i) => (
              <button
                key={title}
                onClick={() => setActiveSubject(i)}
                style={{
                  display:"flex", alignItems:"center", gap:7,
                  background: activeSubject === i ? color : C.white,
                  color: activeSubject === i ? C.white : color,
                  border:`2px solid ${color}`,
                  fontFamily:"Nunito", fontWeight:800, fontSize:12.5,
                  padding:"9px 16px", borderRadius:100, cursor:"pointer", transition:"all 0.2s",
                  boxShadow: activeSubject === i ? `0 4px 14px ${color}45` : "none",
                }}
              ><span>{icon}</span> {title}</button>
            ))}
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <TiltCard maxTilt={2} style={{
            background:C.white, borderRadius:28, padding:"44px 40px",
            boxShadow:"0 14px 40px rgba(0,0,0,0.08)",
            borderTop:`6px solid ${SUBJECTS[activeSubject].color}`,
            transition:"border-top-color 0.3s ease",
          }}>
            <div style={{ display:"flex", gap:24, alignItems:"flex-start", flexWrap:"wrap" }}>
              <div style={{
                width:76, height:76, borderRadius:22, flexShrink:0,
                background:`${SUBJECTS[activeSubject].color}18`,
                display:"flex", alignItems:"center", justifyContent:"center", fontSize:36,
              }}>{SUBJECTS[activeSubject].icon}</div>
              <div style={{ flex:"1 1 320px" }}>
                <div style={{ fontFamily:"Fredoka One", fontSize:26, color:SUBJECTS[activeSubject].color, marginBottom:10, lineHeight:1.2 }}>
                  {SUBJECTS[activeSubject].title}
                </div>
                <p style={{ fontFamily:"Nunito", fontSize:15.5, color:C.text, lineHeight:1.8, margin:"0 0 16px" }}>
                  {SUBJECTS[activeSubject].body}
                </p>
                <div style={{
                  borderLeft:`4px solid ${SUBJECTS[activeSubject].color}`, paddingLeft:16,
                }}>
                  <p style={{ fontFamily:"Lora", fontStyle:"italic", fontSize:15, color:SUBJECTS[activeSubject].color, lineHeight:1.7, margin:0 }}>
                    "{SUBJECTS[activeSubject].why}"
                  </p>
                </div>
              </div>
            </div>
          </TiltCard>
        </FadeIn>

        <div style={{ textAlign:"center", fontFamily:"Nunito", fontSize:12.5, color:C.muted, marginTop:18 }}>
          {activeSubject + 1} of {SUBJECTS.length} — tap another subject above to explore it
        </div>
      </div>
    </section>

    {/* ── Formats ── */}
    <section style={{ background:C.white, padding:"90px 6vw" }}>
      <div style={{ maxWidth:1120, margin:"0 auto" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:36 }}>
            <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:10 }}>Choose Your Format</div>
            <h3 style={{ fontFamily:"Fredoka One", fontSize:"clamp(20px,3vw,30px)", color:C.navy, margin:"0 0 10px" }}>One Curriculum, Four Ways to Reach It.</h3>
            <p style={{ fontFamily:"Nunito", fontSize:14, color:C.muted, maxWidth:560, margin:"0 auto", lineHeight:1.8 }}>Not every family lives around the corner from us — and not every family needs to. The curriculum travels; only the format changes.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:18, alignItems:"start" }}>
            {[
              { label:"Fully Onsite",       color:C.mint,   body:"Five days a week on our school premises — 3 days of academics, 2 days of project-based learning.",
                week:"Mon–Fri, 9 AM–1 PM, all in person. 3 academic days cover Quran, literacy, and maths through the five-dimension framework. 2 PBL days are hands-on — crafts, outdoor time, and the occasional field trip, all together as a class." },
              { label:"Fully Online",       color:C.coral,  body:"All 5 days delivered online, live — a shorter, focused daily session, priced to match.",
                week:"Mon–Fri, live online — a shorter day than onsite, with breaks built in, focused on the academic core rather than the full four-hour rhythm. The same five-dimension curriculum, taught interactively — most hands-on activities use things you already have at home, and any additional materials needed will be communicated to you in advance." },
              { label:"Hybrid for Lahore",  color:C.yellow, body:"3 academic days online, plus 2 project-based learning days onsite with the community.",
                week:"3 days live online for the academic core, then 2 days onsite for project-based learning and community time with the other children — the flexibility of online, without losing the in-person connection." },
              { label:"Homeschooling Support Programme", color:"#8B6BE8", body:"Your personalised weekly homeschool companion — after a one-time assessment, we plan your child's week: timetable, lessons, and activities, so you just teach.", link:"/programs/homeschool-companion" },
            ].map(({ label, color, body, link, week }) => (
              <div key={label} style={{
                background:C.white, borderRadius:16, padding:"22px 20px",
                borderTop:`5px solid ${color}`, boxShadow:"0 4px 14px rgba(0,0,0,0.06)",
                border:`1px solid ${C.warmGray}`, display:"flex", flexDirection:"column",
              }}>
                <div style={{ fontFamily:"Fredoka One", fontSize:16, color:C.navy, marginBottom:10 }}>{label}</div>
                <p style={{ fontFamily:"Nunito", fontSize:13, color:C.muted, lineHeight:1.7, margin:0, flex:1 }}>{body}</p>
                {link && (
                  <Link to={link} style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12.5, color, textDecoration:"none", marginTop:14 }}>
                    Learn more →
                  </Link>
                )}
                {week && (
                  <>
                    <button
                      onClick={() => setOpenFormat(openFormat === label ? null : label)}
                      style={{
                        background:"none", border:"none", cursor:"pointer", padding:0, marginTop:14,
                        fontFamily:"Nunito", fontWeight:700, fontSize:12.5, color, textAlign:"left",
                        display:"flex", alignItems:"center", gap:5,
                      }}
                    >
                      What a week looks like
                      <span style={{ display:"inline-block", transition:"transform 0.2s", transform: openFormat === label ? "rotate(180deg)" : "none" }}>▾</span>
                    </button>
                    {openFormat === label && (
                      <p style={{ fontFamily:"Nunito", fontSize:12.5, color:C.text, lineHeight:1.7, margin:"10px 0 0", paddingTop:10, borderTop:`1px solid ${color}22` }}>
                        {week}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>

    {/* ── Our approach ── */}
    <section style={{ background:C.warmGray, padding:"100px 6vw" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:44 }}>
            <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.navy, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>How We Teach</div>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(26px,4vw,44px)", color:C.navy, margin:"0 0 14px" }}>Mixed-Age, Differentiated, and Never Rushed.</h2>
            <RainbowDivider />
            <p style={{ fontFamily:"Nunito", fontSize:15, color:C.muted, maxWidth:640, margin:"0 auto", lineHeight:1.8 }}>
              Our classroom is Montessori-inspired and mixed-age — children of different ages learning together, each on their own path, through differentiated instruction. No two children in the room are necessarily on the same lesson at the same time, and that's by design.
            </p>
          </div>
        </FadeIn>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:24 }}>
          {PEDAGOGY.map(({ color, icon, title, sub, points }, i) => {
            const pts = Array.isArray(points) ? points : (typeof points === "string" ? points.split("\n").filter(Boolean) : []);
            return (
            <FadeIn key={title} delay={i * 0.1}>
              <TiltCard maxTilt={5} style={{ background:`${color}10`, borderRadius:24, padding:"32px 28px", border:`2px solid ${color}22` }}>
                <div style={{ fontSize:40, marginBottom:16 }}>{icon}</div>
                <div style={{ fontFamily:"Fredoka One", fontSize:22, color, marginBottom:4 }}>{title}</div>
                <div style={{ fontFamily:"Lora", fontStyle:"italic", fontSize:13, color:C.muted, marginBottom:18 }}>{sub}</div>
                {pts.map(pt => (
                  <div key={pt} style={{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:10 }}>
                    <div style={{ width:6, height:6, borderRadius:"50%", background:color, marginTop:7, flexShrink:0 }}/>
                    <span style={{ fontFamily:"Nunito", fontSize:13.5, color:C.text, lineHeight:1.65 }}>{pt}</span>
                  </div>
                ))}
              </TiltCard>
            </FadeIn>
            );
          })}
        </div>
      </div>
    </section>

    {/* ── Bloom's Taxonomy ── */}
    <section style={{ background:C.cream, padding:"90px 6vw" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:44 }}>
            <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.coral, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>How We Assess Understanding</div>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(26px,4vw,44px)", color:C.navy, margin:"0 0 14px" }}>Never Just Memorising.</h2>
            <RainbowDivider />
            <p style={{ fontFamily:"Nunito", fontSize:15, color:C.muted, maxWidth:600, margin:"0 auto", lineHeight:1.8 }}>
              Every concept in the curriculum is taught at all six levels of Bloom's Taxonomy — the same framework behind how we observe and assess each child's progress.
            </p>
          </div>
        </FadeIn>
        <FadeIn delay={0.1}>
          <BloomsTaxonomy />
        </FadeIn>
      </div>
    </section>

    {/* ── What we track ── */}
    <section style={{ background:C.navy, padding:"100px 6vw" }}>
      <div style={{ maxWidth:1100, margin:"0 auto" }}>
        <FadeIn>
          <div style={{ textAlign:"center", marginBottom:56 }}>
            <div style={{ fontFamily:"Nunito", fontWeight:700, fontSize:12, color:C.yellow, letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:12 }}>Included in Enrolment</div>
            <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(26px,4vw,44px)", color:C.white, margin:"0 0 14px" }}>What We Track for Every Child.</h2>
            <RainbowDivider />
          </div>
        </FadeIn>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:18 }}>
          {TRACKING.map(({ icon, color, title, body }) => (
            <FadeIn key={title} delay={0.1}>
              <TiltCard maxTilt={4} style={{ background:C.white, borderRadius:22, padding:"26px 22px", border:`1.5px solid ${color}22`, boxShadow:"0 4px 20px rgba(0,0,0,0.05)" }}>
                <div style={{ fontSize:30, marginBottom:12 }}>{icon}</div>
                <div style={{ fontFamily:"Fredoka One", fontSize:17, color, marginBottom:8 }}>{title}</div>
                <p style={{ fontFamily:"Nunito", fontSize:13, color:C.text, lineHeight:1.7, margin:0 }}>{body}</p>
              </TiltCard>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    {/* ── CTA ── */}
    <section style={{ background:C.cream, padding:"80px 6vw" }}>
      <div style={{ maxWidth:640, margin:"0 auto", textAlign:"center" }}>
        <FadeIn>
          <h2 style={{ fontFamily:"Fredoka One", fontSize:"clamp(24px,3.5vw,38px)", color:C.navy, margin:"0 0 14px" }}>Interested in a Spot?</h2>
          <p style={{ fontFamily:"Nunito", fontSize:15, color:C.muted, lineHeight:1.75, marginBottom:32 }}>With only 15 families in this first cohort, spots are filled with intention. Book a visit to start the conversation.</p>
          <Link to="/admissions" style={{ background:C.navy, color:C.white, textDecoration:"none", fontFamily:"Nunito", fontWeight:800, fontSize:15, padding:"16px 40px", borderRadius:100, boxShadow:"0 6px 24px rgba(27,63,139,0.28)", display:"inline-block" }}>Start Enrolment</Link>
        </FadeIn>
      </div>
    </section>
  </div>
  );
};

export default Preschool;
